import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../state/app.state';
import { CommonService } from '../../../../../../../shared/services/common.service';

@Component({
    selector: 'app-daily-report-expandable-block',
    templateUrl: './daily-report-expandable-block.component.html',
    styleUrls: ['./daily-report-expandable-block.component.scss'],
    standalone: false
})
export class DailyReportExpandableBlockComponent implements OnInit, OnChanges, OnDestroy {
  @Input() icon = '';
  @Input() componentString = '';
  @Input() heading = '';
  @Input() subheading = '';
  @Input() data: any;
  @Input() itemCount: any = 0;
  @Input() isExpanded = false;
  matched: any = [];
  widgetIds: any = [];
  childIdArray: any = [];
  qpReportId: any;
  displayMatched = false;
  matchedReportId: any;
  reportId: any;
  matchedId: any;
  dailyReport: any;
  matchedCount: any;
  matchedOccurrences = 0;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<fromRoot.State>,
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
  ) {
  }

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp && qp['matched']) {
          this.matched = qp['matched'];
        } else {
          this.matched = null;
        }

        if (!Array.isArray(this.matched)) {
          this.matched = [this.matched];
        }

        if (qp && qp['matchedId']) {
          this.matchedId = qp['matchedId'];
          this.matchedReportId = this.matchedId?.[0]?.split('_')?.[0];
        } else {
          this.matchedId = null;
        }

        if (this.matchedId && !Array.isArray(this.matchedId)) {
          this.matchedId = [this.matchedId];
        }

        if (this.matchedId?.length) {
          this.matchedId?.forEach((item) => {
            this.widgetIds?.push(item?.split('_')?.[1]);
          });
        } else {
          this.widgetIds = [];
        }

        if (qp && qp['reportId']) {
          this.qpReportId = qp['reportId'];
        }

        if (this.widgetIds?.length) {
          this.setDisplayMatched();
        }
      });

    this.commonService?.resetSearchContext
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.router.navigate([], {
          queryParams: {
            matched: null,
            matchedId: null,
          },
          queryParamsHandling: 'merge',
        }).then(() => {
          this.displayMatched = false;
          this.cdRef.detectChanges();
        });
      });
  }

  ngOnChanges() {
    this.createChildIdArrays(this.data);
  }

  createChildIdArrays(data) {
    if (this.componentString === 'bid-item') {
      data?.bidItems?.forEach((item: any) => {
        item.lineItems?.forEach((lineItem: any) => {
          this.childIdArray.push(lineItem?.id);
        });
      });
    } else if (this.componentString === 'activities') {
      data?.activities?.forEach((item: any) => {
        this.childIdArray.push(item?.id);
      });
    } else if (this.componentString === 'material-testing') {
      data?.materialTests?.internal?.groupedInternalTests?.forEach((obj: any) => {
        if (obj?.type === 'withStations') {
          obj?.list?.forEach((item) => {
            item?.list?.forEach((o) => {
              this.childIdArray?.push(o?.id);
            });
          });
        } else {
          obj?.list?.forEach((item) => {
            this.childIdArray?.push(item?.id);
          });
        }
      });
    } else if (this.componentString === 'delivery-usage') {

      data?.materialDelivery?.delivery?.forEach((item: any) => {
        this.childIdArray.push(item?.id);
      });

      data?.materialDelivery?.usage?.forEach((item: any) => {
        this.childIdArray.push(item?.id);
      });
    } else if (this.componentString === 'labor-equipment') {

      data?.labors?.forEach((item: any) => {
        this.childIdArray.push(item?.id);
      });

      data?.equipments?.forEach((item: any) => {
        this.childIdArray.push(item?.id);
      });
    } else if (this.componentString === 'control-forms' && this.hasFormMatches()) {
      data?.controlForms?.controlPointForms?.forEach((item: any) => {
        this.childIdArray.push(item?.id);
      });
    } else if (this.componentString === 'forms' && this.hasFormMatches()) {
      data?.controlForms?.dailyReportForms?.forEach((item: any) => {
        this.childIdArray.push(item?.id);
      });
    } else if (this.componentString === 'activity-codes') {
      data?.activity_codes?.forEach((item: any) => {
        this.childIdArray.push(item?.id);
      });
    }

    this.setDisplayMatched();
  }

  /**
   * The `matched` entries are "<component>_<count>" (e.g. "control-forms_1"),
   * so an exact-value check never matched and form widgets were left without
   * any match indicator. Both form widgets share the one `control-forms`
   * component — which of them actually matched is then decided by the id
   * intersection in setDisplayMatched.
   */
  hasFormMatches(): boolean {
    return (this.matched || []).some(item => String(item || '').startsWith('control-forms'));
  }

  setDisplayMatched() {
    if (this.childIdArray?.length && this.widgetIds?.length) {
      const widgetSet = new Set(this.widgetIds);

      if (this.childIdArray.find(id => widgetSet.has(id))) {
        this.displayMatched = true;
      }
    }

    if (this.matched.length > 0) {
      const temp = this.matched?.find(item => item?.includes(this.componentString))?.split('_')?.[1];
      this.matchedCount = +temp;
    }

    // Total keyword hits inside this widget — the same count the matched rows
    // show, summed over the widget's own items ("<reportId>_<itemId>_<count>").
    const ownIds = new Set(this.childIdArray || []);
    this.matchedOccurrences = (this.matchedId || []).reduce((total, entry) => {
      const [, itemId, count] = String(entry || '').split('_');
      return ownIds.has(itemId) ? total + (+count || 0) : total;
    }, 0);

    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
